import {
  Component,
  inject,
  signal,
  computed,
  input,
  output,
  effect,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import {
  FileUploaderComponent
} from '../shared/file-uploader/file-uploader.component';
import { CargaMasivaRepository } from 'src/app/domain/repository/carga-masiva.repository';
import { CargaMasivaResumen } from 'src/app/domain/model/carga-masiva/carga-masiva';
import { crearCitasMasivasResultadoPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BaseCargaMasivaDialog } from './base-carga-masiva-dialog';
import { CargaMasivaResultadoComponent } from './carga-masiva-resultado.component';

/**
 * Resultado del diálogo de creación masiva
 */
export interface CrearCitasMasivasResult {
  /** Indica si se procesó el archivo */
  procesado: boolean;
  /** Resumen del procesamiento */
  resumen?: CargaMasivaResumen;
}

/**
 * CrearCitasMasivasDialogComponent
 *
 * Diálogo para crear citas masivamente desde un archivo Excel.
 * Replica la funcionalidad del legacy LectorCreacionCita.java
 *
 * El archivo se envía directamente al backend que realiza:
 * - Parseo del Excel
 * - Validación de datos
 * - Creación de citas
 *
 * @example
 * ```html
 * <app-crear-citas-masivas-dialog
 *   [visible]="showDialog()"
 *   (onClose)="closeDialog()"
 *   (onResult)="handleResult($event)">
 * </app-crear-citas-masivas-dialog>
 * ```
 *
 * @author GIROP Migration
 * @since 2.0
 */
@Component({
  selector: 'app-crear-citas-masivas-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    MessageModule,
    TooltipModule,
    ProgressBarModule,
    TableModule,
    TagModule,
    FileUploaderComponent,
    CargaMasivaResultadoComponent
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      [header]="'Programación Masiva - Crear Citas'"
      [visible]="dialogVisible()"
      [modal]="true"
      [closable]="!procesando()"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '92vw', maxWidth: '50rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      (visibleChange)="onDialogVisibleChange($event)"
    >
      <!-- Estado inicial: Carga de archivo -->
      @if (estado() === 'cargar') {
        <div class="mb-4">
          <p class="text-gray-600 mb-4">
            Seleccione un archivo Excel (.xls o .xlsx) con los datos de las
            citas a crear. El archivo debe seguir la estructura de la plantilla.
          </p>

          <app-file-uploader
            #fileUploader
            [templateUrl]="plantillaUrl"
            [templateFileName]="'Plantilla_CreacionCita.xlsx'"
            [maxFileSize]="10 * 1024 * 1024"
            (onFileSelected)="onFileSelected($event)"
            (onFileRemoved)="onFileRemoved()"
            (onError)="onFileError($event)"
          >
          </app-file-uploader>
        </div>

        <!-- Mensaje de error -->
        @if (error()) {
          <p-message
            severity="error"
            [text]="error()!"
            styleClass="mt-4 w-full"
          >
          </p-message>
        }

        <!-- Resultados del procesamiento -->
        @if (resultado()) {
          <app-carga-masiva-resultado
            [resumen]="resultado()!"
            [tab]="tabActivo()"
            [onSetTab]="setTabActivo"
            [tableConfig]="resultadosTableConfig()"
            [labels]="resultadoLabels"
          ></app-carga-masiva-resultado>
        }

      }

      <!-- Estado procesando -->
      @if (estado() === 'procesando') {
        <div class="text-center py-8">
          <i class="pi pi-spin pi-spinner text-5xl text-blue-500 mb-4"></i>
          <p class="text-lg font-semibold">Procesando archivo...</p>
          <p class="text-gray-500">Se está validando y creando las citas</p>
          <p-progressBar [mode]="'indeterminate'" styleClass="mt-4 h-2">
          </p-progressBar>
        </div>
      }

      <ng-template pTemplate="footer">
        @if (estado() === 'cargar') {
          <div
            class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
          >
            @if (resultado()) {
              <button
                type="button"
                pButton
                class="p-button-outlined p-button-secondary min-h-[44px]"
                label="Cerrar"
                (click)="cerrarConResultado()"
              ></button>
              <button
                type="button"
                pButton
                class="p-button-outlined p-button-primary min-h-[44px]"
                label="Cargar otro archivo"
                severity="primary"
                (click)="reiniciar()"
              ></button>
            } @else {
              <button
                type="button"
                pButton
                class="p-button-outlined p-button-secondary min-h-[44px]"
                label="Cerrar"
                (click)="cerrar()"
              ></button>
              <button
                type="button"
                pButton
                class="p-button-success min-h-[44px]"
                severity="success"
                label="Procesar"
                icon="pi pi-upload"
                [disabled]="!archivoSeleccionado()"
                [loading]="procesando()"
                (click)="procesar()"
              ></button>
            }
          </div>
        }
      </ng-template>
    </p-dialog>
  `,
  styles: [
    `
      :host ::ng-deep .p-datatable {
        border: 1px solid #e5e7eb;
        border-radius: 0.5rem;
        overflow: hidden;
      }

      :host ::ng-deep .p-datatable-thead > tr > th {
        background: #f9fafb;
      }

      :host ::ng-deep .tabla-resultados .p-datatable-thead > tr > th {
        background: #f3f4f6;
        color: #111827;
        border-color: #d1d5db;
      }

      :host-context(.dark)
        ::ng-deep
        .tabla-resultados
        .p-datatable-thead
        > tr
        > th {
        background: #1f2937;
        color: #f9fafb;
        border-color: #374151;
      }
    `
  ]
})
export class CrearCitasMasivasDialogComponent extends BaseCargaMasivaDialog {
  private readonly cargaMasivaRepository = inject(CargaMasivaRepository);

  /** Referencia al componente FileUploader */
  @ViewChild('fileUploader') fileUploader!: FileUploaderComponent;

  // ==================== INPUTS/OUTPUTS ====================

  /** Controla la visibilidad del diálogo (input del padre) */
  visible = input<boolean>(false);

  /** Señal interna para controlar visibilidad del diálogo */
  dialogVisible = signal(false);

  /** Emite cuando el diálogo se cierra */
  onClose = output<void>();

  /** Emite el resultado del procesamiento */
  onResult = output<CrearCitasMasivasResult>();

  constructor() {
    super();
    // Sincroniza el input visible con la señal interna
    effect(() => {
      this.dialogVisible.set(this.visible());
    });
  }

  readonly resultadosTableConfig = computed(() =>
    crearCitasMasivasResultadoPreset.createConfig({
      data: this.filasVisibles() as unknown as Record<string, unknown>[],
      style: {
        tableClassName: 'tabla-resultados',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.procesando(),
        emptyMessage: 'No hay filas para mostrar'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '72rem',
        scrollHeight: '250px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de resultados de creacion masiva de citas',
        announceSelection: false,
        announcePageChanges: false,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  // ==================== CONFIGURACION ====================

  readonly resultadoLabels = {
    total: 'Total filas',
    exitosos: 'Exitosas',
    errores: 'Con errores',
    principal: 'Orden',
    estadoExitoso: 'Creada',
    btnTodos: 'Todos',
    btnExitosos: 'Exitosos',
    btnErrores: 'Con errores',
    tituloPrincipal: 'Orden'
  };

  readonly setTabActivo = (tab: 'todos' | 'exitosos' | 'errores'): void => {
    this.tabActivo.set(tab);
  };

  /** URL de la plantilla */
  get plantillaUrl(): string {
    return this.cargaMasivaRepository.getPlantillaUrl('creacion');
  }

  // ==================== METODOS ====================

  /**
   * Envía el archivo al backend para procesamiento
   */
  procesar(): void {
    this.procesarArchivo(
      (archivo) => this.cargaMasivaRepository.crearCitasMasivamente(archivo),
      {
        logTag: 'CARGA-MASIVA-CREACION',
        errorFallbackMessage: 'Error al procesar el archivo'
      }
    );
  }

  /**
   * Reinicia el diálogo para cargar otro archivo
   */
  reiniciar(): void {
    this.reiniciarEstado(() => {
      if (this.fileUploader) {
        this.fileUploader.clear();
      }
    });
  }

  /**
   * Maneja el cambio de visibilidad del diálogo (desde PrimeNG)
   */
  onDialogVisibleChange(visible: boolean): void {
    if (!visible && !this.procesando()) {
      this.cerrar();
    }
  }

  /**
   * Cierra el diálogo sin resultado
   */
  cerrar(): void {
    if (!this.procesando()) {
      this.dialogVisible.set(false);
      this.reiniciar();
      this.onClose.emit();
    }
  }

  /**
   * Cierra el diálogo con resultado
   */
  cerrarConResultado(): void {
    const res = this.resultado();
    this.onResult.emit({
      procesado: true,
      resumen: res ?? undefined
    });
    this.dialogVisible.set(false);
    this.reiniciar();
    this.onClose.emit();
  }
}
